/**
 * Produtos e modalidades.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { api } from '../client';
import type { Money } from './comum';

/** Grupo de credito da modalidade (00018). Nulo e nao classificado. */
export type ModalityCreditGroup =
  'tradicional' | 'estruturadas' | 'judicial' | 'recursal' | 'financeira';

export interface Modality {
  id: string;
  product_id: string;
  code: string;
  name: string;
  description: string;
  status: string;
  base_rate_bps: number;
  /**
   * Grupo e referencia de credito do motor (00018). O tipo do frontend nao os tinha,
   * e por isso nao apareciam na tela (defeito 7 de "Produto, taxas, agravos e
   * integracoes" do anexo C do 47).
   */
  credit_group?: ModalityCreditGroup | null;
  credit_reference?: string | null;
  /** Grupo de modalidades do ramo (P-PAR-1). Nulo e sem grupo. */
  modality_group_id?: string | null;
  /** "Codigo de referencia da seguradora" do OnPoint. */
  insurer_reference_code?: string | null;
  susep_process_number?: string | null;
  /**
   * Quem pede criou, carregou ou editou o rascunho e por isso nao o ativa (P-CARGA).
   * Falso fora de rascunho. Orienta a tela; quem decide e o servidor.
   */
  draft_authored_by_you?: boolean;
  /**
   * Quem pede e autor do rascunho e foi liberado nominalmente da segregacao (ADR
   * 0091): pode ativa-lo mesmo assim, como autoaprovacao.
   */
  draft_may_self_approve?: boolean;
  /**
   * A configuracao ativa usa taxa por modalidade. So entao a ativacao exige taxa base
   * e a operacao sugere a taxa base; sem ela, a taxa vem do tomador ou da analise.
   */
  uses_modality_rate?: boolean;
  created_at?: string;
  updated_at?: string;
}

/** Grupo de modalidades do ramo (Judicial, Tradicional), com a contagem. */
export interface ModalityGroup {
  id: string;
  product_id: string;
  name: string;
  credit_group: string | null;
  status: string;
  /** Guardado e sem efeito ate a homologacao de LIM-002. */
  default_economic_group_limit: Money | null;
  ignore_branch_for_individual_insured: boolean;
  ignore_branch_for_private_insured: boolean;
  external_product_code: string | null;
  /**
   * Descricao do grupo (ModalityGroupDescription do OnPoint, TOM-645). Opcional para
   * respostas antigas.
   */
  description?: string;
  modality_count: number;
  /**
   * Quem pede criou, carregou ou editou o rascunho e por isso nao o ativa (P-CARGA).
   * Falso fora de rascunho. Orienta a tela; quem decide e o servidor.
   */
  draft_authored_by_you?: boolean;
  /**
   * Quem pede e autor do rascunho e foi liberado nominalmente da segregacao (ADR
   * 0091): pode ativa-lo mesmo assim, como autoaprovacao.
   */
  draft_may_self_approve?: boolean;
  created_at?: string;
  updated_at?: string;
}

/** Tipo do ramo (EType do OnPoint), a confirmar. */
export type BranchKind = 'setor_publico' | 'setor_privado';

export interface Product {
  id: string;
  code: string;
  name: string;
  description: string;
  status: string;
  /** O ramo do OnPoint (P-PAR-1). Nulo e nao informado. */
  susep_branch_code?: string | null;
  susep_group_code?: string | null;
  susep_process_number?: string | null;
  branch_kind?: BranchKind | null;
  modality_groups?: ModalityGroup[];
  modalities: Modality[];
  /**
   * Quem pede criou, carregou ou editou o rascunho e por isso nao o ativa (P-CARGA).
   * Falso fora de rascunho. Orienta a tela; quem decide e o servidor.
   */
  draft_authored_by_you?: boolean;
  /**
   * Quem pede e autor do rascunho e foi liberado nominalmente da segregacao (ADR
   * 0091): pode ativa-lo mesmo assim, como autoaprovacao.
   */
  draft_may_self_approve?: boolean;
}

export interface ProductInput {
  code: string;
  name: string;
  description?: string;
  status?: string;
  susep_branch_code?: string;
  susep_group_code?: string;
  susep_process_number?: string;
  branch_kind?: string;
}

export interface ModalityInput {
  code: string;
  name: string;
  description?: string;
  status?: string;
  base_rate_bps: number;
  credit_group?: string;
  credit_reference?: string;
  /** Identificador do grupo do ramo, ou vazio para sem grupo. */
  modality_group_id?: string;
  insurer_reference_code?: string;
  susep_process_number?: string;
}

/**
 * Situacao do ramo, do grupo e da modalidade. O rascunho (P-CARGA) nao entra na
 * cotacao e so sai dele pela ativacao, feita por outra pessoa com justificativa.
 */
export type CatalogStatus = 'rascunho' | 'ativo' | 'inativo';

export const products = {
  list: (signal?: AbortSignal) => api.get<Product[]>('/api/v1/products', signal),
  create: (input: ProductInput) => api.post<Product>('/api/v1/products', input),
  update: (id: string, input: ProductInput) =>
    api.patch<Product>(`/api/v1/products/${id}`, input),
  /** Exclui o produto sem nada ligado; o produto em uso responde PRODUTO_EM_USO. */
  remove: (id: string) => api.delete<void>(`/api/v1/products/${id}`),
  createModality: (productId: string, input: ModalityInput) =>
    api.post<Modality>(`/api/v1/products/${productId}/modalities`, input),
  updateModality: (productId: string, modalityId: string, input: ModalityInput) =>
    api.patch<Modality>(
      `/api/v1/products/${productId}/modalities/${modalityId}`,
      input,
    ),
};
